import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { startWith } from 'rxjs';

import { IconDirective } from '../../icon/icon.directive';

/** Custom header component for datepicker. */
@Component({
  selector: 'tt-datepicker-header',
  styleUrls: ['datepicker-header.component.scss'],
  template: `
    <div class="tt-datepicker-header">
      <div class="tt-datepicker-header_button" (click)="previousClicked()"><mat-icon [icon]="'chevron-left'" [iconSize]="18" /></div>
      <span class="tt-datepicker-header_label" (click)="periodLabelClicked()">{{ periodLabel() }}</span>
      <div class="tt-datepicker-header_button" (click)="nextClicked()"><mat-icon [icon]="'chevron-right'" [iconSize]="18" /></div>
    </div>
  `,
  standalone: true,
  imports: [MatButtonModule, MatIconModule, IconDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerHeaderComponent<D> {
  private _calendar = inject<MatCalendar<D>>(MatCalendar);
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter);
  private _dateFormats = inject(MAT_DATE_FORMATS);

  readonly periodLabel = signal('');

  constructor() {
    this._calendar.stateChanges.pipe(startWith(null), takeUntilDestroyed()).subscribe(() => {
      const monthLabel = this._dateAdapter.getMonthNames('long')[this._dateAdapter.getMonth(this._calendar.activeDate)];
      const yearLabel = this._dateAdapter.getYearName(this._calendar.activeDate);
      this.periodLabel.set(`${monthLabel} ${yearLabel}`);
    });
  }

  protected previousClicked() {
    this._calendar.activeDate =
      this._calendar.currentView === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  protected nextClicked() {
    this._calendar.activeDate =
      this._calendar.currentView === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }

  protected periodLabelClicked() {
    this._calendar.currentView = 'year';
  }
}
